import type { HandbookChapter } from '../model';

/** Was der Kunde sieht -- und was die Prüfung sehen will. */

const portal: HandbookChapter = {
  id: 'portal',
  title: 'Kundenportal und Web-Formulare',
  summary: 'Was Ihre Kunden sehen, wie es aussieht und woher die Anfragen kommen.',
  audience: 'admin',
  route: '/app/administration/portal',
  sections: [
    {
      id: 'was-der-kunde-sieht',
      title: 'Was der Kunde sieht',
      blocks: [
        { kind: 'shot', file: 'portal', caption: 'Das Self-Service-Portal aus Kundensicht.' },
        {
          kind: 'text',
          text: 'Das Portal liegt unter /portal und ist eine getrennte Anwendung. Es liefert niemals Bearbeitercode aus — nicht als Ladezeitoptimierung, sondern weil im Bearbeitercode Feldnamen, Endpunkte und Bedienlogik stehen, die einen Portalnutzer nichts angehen.',
        },
        {
          kind: 'terms',
          items: [
            {
              term: 'Meine Anfragen',
              text: 'Die eigenen Tickets mit Verlauf. Wer die Rolle „Kundenverantwortung“ trägt, sieht alle Tickets seines Kunden.',
            },
            {
              term: 'Neue Anfrage',
              text: 'Betreff und Beschreibung. Beim Tippen erscheinen passende Anleitungen aus der Wissensdatenbank — vielleicht erübrigt sich die Anfrage.',
            },
            {
              term: 'Hilfe',
              text: 'Die Wissensartikel, deren Sichtbarkeit das Portal einschließt.',
            },
            {
              term: 'Rückmeldung',
              text: 'Der Kunde antwortet im Verlauf. Interne Notizen erscheinen dort nicht — sie werden gar nicht erst ausgeliefert.',
            },
          ],
        },
      ],
    },
    {
      id: 'vorgang-ansehen',
      title: 'Vorgang ansehen ohne Zugang',
      blocks: [
        {
          kind: 'shot',
          file: 'portal-vorgangszugang',
          caption: 'Ein Vorgang, geöffnet mit E-Mail-Adresse und Vorgangsnummer.',
        },
        {
          kind: 'text',
          text: 'Die meisten Ihrer Kunden haben keinen Portalzugang — sie haben eine Mail mit einer Vorgangsnummer. Unter /portal genügen deshalb E-Mail-Adresse und Vorgangsnummer, um den eigenen Vorgang zu sehen und darin zu antworten. Ein Konto ist dafür nicht nötig, ein Kennwort auch nicht.',
        },
        {
          kind: 'text',
          text: 'In jeder Mail, die aus einem Vorgang hinausgeht, steht am Ende ein Link dorthin. Er bringt beide Angaben mit: Wer darauf klickt, landet unmittelbar in seinem Vorgang und muss nichts eintippen.',
        },
        {
          kind: 'terms',
          items: [
            {
              term: 'Was der Kunde sieht',
              text: 'Stand, Sachverhalt und den öffentlichen Schriftwechsel — genau das, was er auch im angemeldeten Portal sähe. Interne Notizen erscheinen nicht; sie werden gar nicht erst ausgeliefert.',
            },
            {
              term: 'Immer nur dieser eine Vorgang',
              text: 'Der Zugang öffnet den Vorgang, dessen Nummer genannt wurde — nicht die Liste und nicht den zweiten Vorgang desselben Melders. Wer den Überblick über sein Haus braucht, bekommt einen Portalzugang.',
            },
            {
              term: 'Nach einer Stunde erneut',
              text: 'Danach werden Adresse und Nummer wieder verlangt. Der Link aus der Mail führt in einem Zug zurück.',
            },
            {
              term: 'Nach dem Abschluss noch 30 Tage',
              text: 'Solange an einem Vorgang gearbeitet wird, gilt der Zugang ohnehin. Danach läuft er aus — ein alter Link in einem vergessenen Postfach öffnet dann nichts mehr.',
            },
          ],
        },
        {
          kind: 'note',
          tone: 'warning',
          title: 'Wer die Nummer errät, kommt hinein — deshalb wird gezählt',
          text: 'Vorgangsnummern sind fortlaufend, E-Mail-Adressen stehen auf jeder Visitenkarte. Nach fünf Fehlversuchen ist deshalb eine Viertelstunde Ruhe — gezählt wird je Adresse und je Anschluss. Jeder Versuch, auch der erfolgreiche, steht im Protokoll unter der Art „ticket_access“: Dort sehen Sie, wer wann welchen Vorgang geöffnet hat.',
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Ein Vorgang ohne Melder öffnet sich nie',
          text: 'Verglichen wird die Adresse des Melders im Vorgang. Ein Vorgang aus der Überwachung hat keinen — und bleibt damit zu. Wenn ein Kunde nicht hineinkommt, obwohl er es sollte: Prüfen Sie, ob er als Melder eingetragen ist und ob seine Adresse dort richtig steht.',
        },
      ],
    },
    {
      id: 'auftritt',
      title: 'Den Auftritt einstellen',
      blocks: [
        {
          kind: 'shot',
          file: 'portal-auftritt',
          caption: 'Logo, Farbe und Begrüßung des Portals.',
        },
        {
          kind: 'steps',
          steps: [
            {
              text: 'Adresse des Logos eintragen — muss mit https:// beginnen.',
            },
            { text: 'Markenfarbe setzen.' },
            {
              text: 'Begrüßungstext schreiben.',
              detail:
                'Der erste Satz, den ein Kunde liest. Er darf sagen, wann Sie erreichbar sind und was ins Portal gehört und was ans Telefon.',
            },
          ],
        },
        { kind: 'route', to: '/app/administration/portal', label: 'Zum Kundenportal' },
      ],
    },
    {
      id: 'webformulare',
      title: 'Web-Formulare',
      blocks: [
        {
          kind: 'shot',
          file: 'web-formulare',
          caption: 'Ein Web-Formular mit seinen Feldern und dem Einbettungscode.',
        },
        {
          kind: 'text',
          text: 'Ein Web-Formular liegt auf der Website eines Kunden. Aus jeder Einsendung entsteht ein Ticket — ohne Anmeldung, mit Schutz gegen Roboter und ohne fremdes Captcha.',
        },
        {
          kind: 'steps',
          steps: [
            { text: 'Warteschlange wählen, in der die Tickets entstehen.' },
            {
              text: 'Kunden fest zuordnen.',
              detail:
                'Der Kunde steht fest und kommt nie aus der Einsendung — sonst wäre das Formular ein Weg, Tickets bei fremden Kunden anzulegen.',
            },
            {
              text: 'Herkunft der Website eintragen.',
              detail: 'Nur https, ohne Pfad. Leer heißt: keine Einschränkung durch den Browser.',
            },
            {
              text: 'Felder zusammenstellen.',
              detail: 'Ohne Felder fragt das Formular nur nach Betreff und Text.',
            },
            { text: 'Den Einbettungscode auf der Website hinterlegen.' },
          ],
        },
        {
          kind: 'table',
          head: ['Ergebnis einer Einsendung', 'Was dahintersteckt'],
          rows: [
            ['angenommen', 'Ein Ticket ist entstanden'],
            ['als Roboter erkannt', 'Ein Feld ausgefüllt, das kein Mensch sieht'],
            ['zu schnell abgeschickt', 'Schneller ausgefüllt, als sich lesen lässt'],
            ['Formular veraltet', 'Die Seite lag zu lange offen'],
            ['doppelt abgeschickt', 'Dieselbe Einsendung ein zweites Mal'],
            ['zu viele Einsendungen', 'Mengenbegrenzung gegriffen'],
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          title: '„Im Kundenportal anbieten“',
          text: 'Getrennt von „aktiv“ zu setzen: Ein eingebettetes Kontaktformular fragt nach Angaben, die im Portal längst bekannt sind.',
        },
        { kind: 'route', to: '/app/administration/web-forms', label: 'Zu den Web-Formularen' },
      ],
    },
    {
      id: 'umfrage',
      title: 'Zufriedenheitsumfrage',
      blocks: [
        {
          kind: 'text',
          text: 'Nach dem Abschluss eines Tickets geht eine Umfrage an den Melder. Sie öffnet sich über ein Merkmal im Link, ohne Anmeldung — das Portal verlangt eine Anmeldung, und wer nur eine Note geben soll, soll sich nicht anmelden müssen.',
        },
        {
          kind: 'note',
          tone: 'warning',
          title: 'Sie ist als Vorgabe ausgeschaltet',
          text: 'Eine Umfrage geht an Ihre Kunden hinaus — das ist Ihre Entscheidung und keine, die ein Systemwechsel nebenbei trifft. Einschalten unter Berichte → Kundenzufriedenheit, Kästchen „Zufriedenheitsumfragen versenden".',
        },
        {
          kind: 'terms',
          items: [
            {
              term: 'Erst beim Abschluss, nicht beim Lösen',
              text: 'Ein gelöster Vorgang kann wieder aufgehen; dann wäre die Frage verfrüht oder käme zweimal. Wer auf „gelöst" setzt und auf die Mail wartet, wartet vergeblich — das ist kein Fehler.',
            },
            {
              term: 'Einen Tag später',
              text: 'Die Umfrage entsteht beim Abschluss und geht nach der eingestellten Verzögerung hinaus — ab Werk 24 Stunden. Das erledigt der Hintergrunddienst von selbst; er sieht jede Minute nach. „Wartende Umfragen jetzt versenden“ verschickt alle wartenden sofort, ohne die Verzögerung abzuwarten.',
            },
            {
              term: 'Nur ein Teil der Vorgänge',
              text: 'Ab Werk wird zu einem Drittel der abgeschlossenen Vorgänge gefragt — wer nach jedem Vorgang fragt, bekommt bald keine Antwort mehr. Welche Vorgänge dazugehören, ergibt sich fest aus der Ticketnummer; kein Vorgang wird zweimal gefragt. Schließen Sie zum Ausprobieren also mehrere Vorgänge, oder stellen Sie den Anteil vorübergehend auf 100 Prozent.',
            },
            {
              term: 'Nicht rückwirkend',
              text: 'Was vor dem Einschalten abgeschlossen wurde, bekommt keine Umfrage. Zum Ausprobieren also einen Vorgang schließen, nachdem der Schalter steht.',
            },
            {
              term: 'Wer nicht gefragt wird',
              text: 'Vorgänge ohne Melder, Melder ohne Adresse, Kunden ohne Mailversand und Ansprechpartner mit Widerspruch nach Art. 21 DSGVO. Jeder dieser Fälle ist der Regelfall und kein Fehler.',
            },
          ],
        },
        {
          kind: 'shot',
          file: 'umfragen',
          caption:
            'Der Bericht „Kundenzufriedenheit“ mit dem Stand der Umfragen und ihren Einstellungen.',
        },
        {
          kind: 'terms',
          items: [
            {
              term: 'Warten auf Versand',
              text: 'Angelegt, aber noch nicht verschickt. Darunter steht, wann die nächste von selbst hinausgeht.',
            },
            {
              term: 'Verschickt, noch ohne Antwort',
              text: 'Beim Kunden angekommen, noch nicht bewertet.',
            },
            {
              term: 'Beantwortet',
              text: 'Mit dem Rücklauf: welcher Anteil der verschickten Umfragen beantwortet wurde. Ein Mittelwert aus zwei Antworten bei achtzig Befragten ist kein Mittelwert, sondern zwei freundliche Menschen.',
            },
          ],
        },
        {
          kind: 'text',
          text: 'Die Bewertungen selbst stehen im Bericht „Kundenzufriedenheit“ darüber. Er zählt nur beantwortete Umfragen und bleibt leer, bis jemand antwortet — auch wenn schon viele verschickt sind.',
        },
        {
          kind: 'text',
          text: 'Verzögerung und Anteil stellen Sie unter dem Schalter ein; sie erscheinen, sobald die Umfragen eingeschaltet sind. Beides braucht das Recht, die Stammdaten zu pflegen, ebenso der Knopf zum sofortigen Versand.',
        },
        { kind: 'route', to: '/app/reports', label: 'Zu den Berichten' },
      ],
    },
  ],
};

const datenschutz: HandbookChapter = {
  id: 'datenschutz',
  title: 'Datenschutz',
  summary: 'Betroffenenrechte, Aufbewahrung und das Feldverzeichnis.',
  audience: 'privacy',
  route: '/app/administration/privacy',
  sections: [
    {
      id: 'einordnung',
      title: 'Einordnung',
      blocks: [
        {
          kind: 'shot',
          file: 'datenschutz',
          caption: 'Die vier Bereiche der Datenschutzwerkzeuge.',
        },
        {
          kind: 'text',
          text: 'Diese Werkzeuge tragen ein eigenes Recht (privacy.manage) und einen eigenen Abschnitt in der Navigation. Wer sie benutzt, richtet nichts ein — aber er arbeitet auch nicht am Ticket. Sie gehören zu den Vorgängen, die der Betreiber führt, und nicht zu denen, die der Bearbeiter führt.',
        },
      ],
    },
    {
      id: 'ersuchen',
      title: 'Betroffenenersuchen',
      blocks: [
        {
          kind: 'table',
          head: ['Art', 'Was zu tun ist'],
          rows: [
            ['Auskunft (Art. 15)', 'Auskunftsexport erzeugen und übergeben'],
            ['Berichtigung (Art. 16)', 'Die betroffenen Angaben richtigstellen'],
            ['Löschung (Art. 17)', 'Ansprechpartner anonymisieren, Aufbewahrungsfristen prüfen'],
            ['Datenübertragbarkeit (Art. 20)', 'Auskunft als JSON übergeben'],
            [
              'Widerspruch (Art. 21)',
              'Am Ansprechpartner vermerken — Umfragen und Benachrichtigungen hören auf',
            ],
            ['Einschränkung (Art. 18)', 'Verarbeitung anhalten, Vorgang dokumentieren'],
          ],
        },
        {
          kind: 'note',
          tone: 'danger',
          title: 'Die Frist wird beim Eingang gerechnet',
          text: 'Und sie lässt sich danach nicht verschieben (Art. 12 Abs. 3 DSGVO). Nehmen Sie ein Ersuchen an dem Tag auf, an dem es eingeht — nicht an dem, an dem Sie dazu kommen. Überfällige Ersuchen sind in der Liste gekennzeichnet.',
        },
        {
          kind: 'steps',
          steps: [
            { text: '„Ersuchen aufnehmen“ klicken.' },
            {
              text: 'Art, betroffene Person und Eingangsdatum eintragen.',
              detail:
                'Das Feld „Betroffene Person“ schlägt Ansprechpartner aus dem CRM vor. Steht die Person dort nicht, tippen Sie ihre Adresse einfach ein — ein Ersuchen kommt nicht nur von Kontakten.',
            },
            { text: 'Vermerken, was veranlasst wurde.' },
            { text: 'Auf „abgeschlossen“ setzen, wenn beantwortet — mit dem Vermerk, wie.' },
          ],
        },
      ],
    },
    {
      id: 'auskunft',
      title: 'Auskunft erteilen',
      blocks: [
        {
          kind: 'text',
          text: 'Der Auskunftsexport zieht alles zusammen, was über eine Person gespeichert ist: Stammdaten, Tickets, Beiträge, Zeiteinträge, Protokolleinträge. Als PDF für die Übergabe an die Person, als JSON für die Datenübertragbarkeit nach Art. 20.',
        },
      ],
    },
    {
      id: 'aufbewahrung',
      title: 'Aufbewahrung',
      blocks: [
        {
          kind: 'table',
          head: ['Frist', 'Worauf sie sich bezieht'],
          rows: [
            ['Abgeschlossene Tickets', 'Zehn Jahre orientieren sich an § 147 AO'],
            ['Zufriedenheitsumfragen', 'Rückmeldungen nach Ticketabschluss'],
            ['Ruhende Ansprechpartner', 'Kontakte ohne Vorgang'],
            ['Anhänge', 'Dateien an Beiträgen'],
            ['Protokolleinträge', 'Das Änderungsprotokoll selbst'],
            ['Fehlgeschlagene Anmeldungen', 'Der Sicherheitsnachweis'],
          ],
        },
        {
          kind: 'note',
          tone: 'danger',
          title: 'Immer zuerst der Probelauf',
          text: 'Der Probelauf zählt und ändert nichts. Wer eine Frist scharf schaltet, ohne sie vorher gezählt zu haben, erfährt von einem Zahlendreher, wenn die Daten fort sind. Der scharfe Lauf entfernt und anonymisiert endgültig.',
        },
        {
          kind: 'text',
          text: 'Fristen und Lauf stehen im selben Reiter „Aufbewahrung“: oben die Fristen, darunter der Lauf, der sie anwendet. Wer eine Frist ändert, sieht den Probelauf gleich darunter.',
        },
        {
          kind: 'text',
          text: 'Der automatische Löschlauf ist als Vorgabe aus — und das mit Absicht. Schalten Sie ihn ein, nachdem Sie den Probelauf gesehen haben.',
        },
      ],
    },
    {
      id: 'protokoll',
      title: 'Das Protokoll',
      blocks: [
        {
          kind: 'text',
          text: 'Jede Änderung an Nutzdaten steht im Protokoll: wer, wann, welches Objekt, welches Feld, alter und neuer Wert. Es ist nachträglich nicht änderbar. Gelesen, gefiltert und als CSV ausgegeben wird es auf der Seite „Audit“ direkt unter „Datenschutz“ — das nächste Kapitel beschreibt sie.',
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Wofür das Protokoll da ist',
          text: 'Es ist die Grundlage für die Auskunft nach Art. 15 DSGVO und für den Nachweis nach ISO 27001. Es ist kein Überwachungswerkzeug für die Arbeit der Bearbeiter — und sollte auch nicht als solches benutzt werden.',
        },
      ],
    },
    {
      id: 'feldverzeichnis',
      title: 'Das Feldverzeichnis',
      blocks: [
        {
          kind: 'text',
          text: 'Es zeigt, welche Felder das System als personenbezogen führt und wie sie bei einer Anonymisierung behandelt werden.',
        },
        {
          kind: 'terms',
          items: [
            { term: 'wird ersetzt', text: 'Der Wert weicht einem Platzhalter.' },
            { term: 'wird geleert', text: 'Der Wert entfällt.' },
            { term: 'bleibt stehen', text: 'Ohne Personenbezug, wird nicht angetastet.' },
            {
              term: 'Freitext — von Hand prüfen',
              text: 'Ein Beschreibungsfeld kann einen Namen enthalten, muss aber nicht. Hier muss ein Mensch nachsehen. Das System behauptet nicht, es könne das entscheiden.',
            },
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Automatisch angelegte Ansprechpartner',
          text: 'Beim Ansprechpartner steht „Aus einer eingehenden Mail entstanden (Herkunft)“. Das Feld sagt, dass der Eintrag nicht bei der betroffenen Person erhoben wurde, sondern aus einer Mail an eine hinterlegte E-Mail-Domäne — genau die Angabe, die Art. 15 Abs. 1 Buchst. g DSGVO verlangt. Es bleibt deshalb auch bei der Anonymisierung stehen. Löschfrist und Betroffenenrechte gelten unverändert.',
        },
        { kind: 'route', to: '/app/administration/privacy', label: 'Zu den Datenschutzwerkzeugen' },
      ],
    },
  ],
};

const audit: HandbookChapter = {
  id: 'audit',
  title: 'Audit',
  summary: 'Wer hat wann was geändert — lesen, einschränken, im Einzelnen nachsehen.',
  audience: 'privacy',
  route: '/app/administration/audit',
  sections: [
    {
      id: 'grundsatz',
      title: 'Was das Protokoll ist',
      blocks: [
        { kind: 'shot', file: 'audit', caption: 'Die Auditansicht mit ihren Filtern.' },
        {
          kind: 'text',
          text: 'Jede Änderung an Nutzdaten wird festgehalten: wer, wann, welches Objekt, welches Feld, alter und neuer Wert. Das geschieht ohne Zutun — es gibt keinen Schalter, der es einschaltet, und keinen, der es abschaltet. Ein neues Modul protokolliert vom ersten Tag an mit.',
        },
        {
          kind: 'note',
          tone: 'danger',
          title: 'Nachträglich nicht änderbar',
          text: 'Die Anwendung darf auf dieser Tabelle weder ändern noch löschen — das ist in der Datenbank festgelegt und nicht nur im Programm. Es gibt deshalb in dieser Ansicht keinen Knopf, der schreibt. Ein Protokoll, das der protokollierende Dienst nachträglich verändern kann, ist als Nachweis wertlos.',
        },
        {
          kind: 'text',
          text: 'Bereinigt wird ausschließlich über die Aufbewahrungsfristen im Datenschutzbereich — als Lauf, der selbst protokolliert wird, und nicht von Hand aus dieser Maske heraus.',
        },
      ],
    },
    {
      id: 'lesen',
      title: 'Eine Zeile lesen',
      blocks: [
        {
          kind: 'terms',
          items: [
            {
              term: 'Kurzbeschreibung',
              text: 'Was geschehen ist, als Satz: „T-2026-000123: Status geändert von ›Offen‹ auf ›In Bearbeitung‹“. Verweise sind aufgelöst — es steht der Name des Zustands da und nicht seine Kennung.',
            },
            { term: 'Zeitpunkt', text: 'Datum und Uhrzeit in Ihrer Zeitzone.' },
            {
              term: 'Bearbeiter',
              text: 'Der aufgelöste Name. Bei System- oder Tokenvorgängen steht dort die Art statt eines Namens.',
            },
            { term: 'Modul', text: 'Aus welchem Bereich der Vorgang stammt.' },
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Werte erst im Einzelnen',
          text: 'Die Liste nennt, was geschehen ist; die alten und neuen Werte stehen in der Detailansicht. Eine Liste, die sie offen zeigt, ließe sich mit einem Blick als Personenverzeichnis überfliegen.',
        },
      ],
    },
    {
      id: 'filtern',
      title: 'Einschränken',
      blocks: [
        {
          kind: 'text',
          text: 'Zeitraum, Modul, Bearbeiter und Aktion. Modul, Bearbeiter und Aktion nehmen mehrere Werte; zwischen den Gruppen gilt UND. Jede Änderung an einem Filter beginnt wieder auf der ersten Seite.',
        },
        {
          kind: 'text',
          text: '„Als CSV ausgeben“ oben rechts liefert genau den eingeschränkten Ausschnitt als Datei, höchstens zehntausend Zeilen — mit allen Feldänderungen, die die Liste erst in der Detailansicht zeigt. Das ist der Auszug für eine Prüfung.',
        },
        {
          kind: 'example',
          title: 'Eine typische Frage',
          text: '„Was hat diese Woche jemand aus dem Team an Tickets und Kunden geändert?“ — Zeitraum „Letzte 7 Tage“, Module „Tickets“ und „Kunden“, Bearbeiter ankreuzen. Drei Gruppen, mehrere Werte je Gruppe.',
        },
        {
          kind: 'note',
          tone: 'warning',
          title: 'Keine Volltextsuche über die Werte',
          text: 'Eine freie Suche über alte und neue Werte wäre ein Werkzeug, mit dem sich das Protokoll nach Personen durchsuchen ließe, ohne dass es auffällt. Deshalb gibt es sie nicht.',
        },
      ],
    },
    {
      id: 'detail',
      title: 'Die Detailansicht',
      blocks: [
        {
          kind: 'text',
          text: '„Details“ öffnet den Eintrag mit allen Feldänderungen — alt und neu je Feld —, der Vorgangskennung und der IP-Adresse. Mit den beiden letzten lässt sich derselbe Vorgang im Serverprotokoll wiederfinden; das ist bei einer Prüfung die zweite Frage.',
        },
        {
          kind: 'note',
          tone: 'success',
          title: 'Kennwörter erscheinen nie',
          text: 'Kennwortabdrücke, TOTP-Geheimnisse und Tokenabdrücke werden redigiert: Dass sich das Feld geändert hat, steht da — womit, nie.',
        },
        {
          kind: 'text',
          text: 'Die letzten Vorgänge stehen zusätzlich auf der Startseite unter „Letzte Aktivitäten“, in derselben Darstellung. Ein Klick darauf öffnet dieselbe Detailansicht.',
        },
        { kind: 'route', to: '/app/administration/audit', label: 'Zum Audit' },
      ],
    },
  ],
};

export const kundeChapters: HandbookChapter[] = [portal, datenschutz, audit];
